import { useState } from "react";
import { Modal, ModalContent, ModalTitle } from "@safers/ui/organisms";
import { Mic } from "@safers/ui/atoms";
import { useCommandSubmit } from "@/hooks/useCommandSubmit";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { RecordingIndicator } from "./RecordingIndicator";

interface CommandModalProps {
  open: boolean;
  onClose: () => void;
}

export function CommandModal({ open, onClose }: CommandModalProps) {
  const [value, setValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const { submit, isSubmitting } = useCommandSubmit();
  const { startListening, cancelListening, isSupported } = useVoiceRecognition();

  const handleClose = () => {
    cancelListening();
    setIsRecording(false);
    setValue("");
    onClose();
  };

  // 텍스트 Enter / 음성 확정 공통 경로
  const runCommand = async (text: string) => {
    if (!text.trim()) return;
    await submit(text);
    handleClose();
  };

  const handleMicClick = () => {
    if (isRecording) {
      cancelListening();
      return;
    }
    startListening({
      onStart: () => {
        setIsRecording(true);
        console.log("[음성] 인식 시작");
      },
      onResult: (transcript, isFinal) => {
        setValue(transcript);
        console.log(isFinal ? "[음성] 인식 확정:" : "[음성] 인식 중:", transcript);
        if (isFinal) runCommand(transcript);
      },
      onEnd: () => {
        setIsRecording(false);
        console.log("[음성] 인식 종료");
      },
      onError: (error) => {
        setIsRecording(false);
        if (error === "aborted") return;
        console.log("[음성] 에러:", error);
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runCommand(value);
  };

  return (
    <Modal open={open} onOpenChange={(next) => !next && handleClose()}>
      <ModalContent
        showClose={false}
        aria-describedby={undefined} // 설명(Description) 없음을 명시 → Radix 경고 제거
        className="top-[18vh] w-[40rem] max-w-[calc(100vw-2rem)] translate-y-0 overflow-hidden p-0"
        onInteractOutside={(e) => {
          if (isRecording) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isRecording) {
            e.preventDefault();
            cancelListening();
          }
        }}
      >
        <ModalTitle className="sr-only">명령 검색</ModalTitle>

        <form onSubmit={handleSubmit} className="flex items-center gap-2 px-5 py-4">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={isSubmitting}
            placeholder={
              isRecording ? "듣고 있어요…" : "현장, 카메라, 날씨 등 Safers AI 에게 물어보기…"
            }
            aria-label="명령 입력"
            className="min-w-0 flex-1 bg-transparent text-base text-neutral-800 outline-none placeholder:text-neutral-400 disabled:opacity-60"
          />

          {isSupported && (
            <button
              type="button"
              onClick={handleMicClick}
              disabled={isSubmitting}
              aria-label={isRecording ? "음성 인식 중지" : "음성 입력 시작"}
              className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-50 ${
                isRecording
                  ? "border-red-500 bg-red-500 text-white"
                  : "border-neutral-200 text-neutral-400 hover:border-brand hover:bg-neutral-100 hover:text-brand"
              }`}
            >
              {isRecording && (
                <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40" />
              )}
              <Mic size="sm" className="relative" />
            </button>
          )}

          {isSubmitting ? (
            <span
              className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-neutral-200 border-t-brand"
              aria-label="처리 중"
            />
          ) : (
            <kbd className="shrink-0 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs font-medium text-neutral-400">
              ESC
            </kbd>
          )}
        </form>

        <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-2.5 text-xs text-neutral-400">
          <span>
            <kbd className="mr-1 rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5">
              Enter
            </kbd>
            실행
          </span>
          <span className="font-semibold tracking-wider text-neutral-300">SAFERS COMMAND</span>
        </div>
      </ModalContent>

      {isRecording && <RecordingIndicator transcript={value} onStop={cancelListening} />}
    </Modal>
  );
}
